import { SCORING_PRIORITIES } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import { LeadPriorityScore } from "app/pages/sales-intelligence/modules/lists/types/columns";
import {
    type IScoringRuleDto,
    type ScoringConfigRule,
    ScoringConfigType,
} from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";

/** @deprecated Use `SCORING_PRIORITIES` from `domain/leads/types/lead-priority` — the canonical, domain-level list. */
export const SCORING_PRIORITY_COLUMNS: readonly LeadPriorityScore[] = [
    LeadPriorityScore.High,
    LeadPriorityScore.Medium,
    LeadPriorityScore.Low,
];

const replaceScoringColumn = (
    rules: ScoringConfigRule,
    priority: LeadPriorityScore,
    nextColumn: readonly IScoringRuleDto[],
): ScoringConfigRule => ({
    ...rules,
    [priority]: nextColumn,
});

export const findScoringRuleById = (rules: ScoringConfigRule, ruleId: string): IScoringRuleDto | undefined => {
    for (const priorityRules of Object.values(rules)) {
        const match = priorityRules?.find((rule) => rule.id === ruleId);

        if (match) {
            return match;
        }
    }

    return undefined;
};

export const resolveEffectiveSelectedRuleId = (
    rules: ScoringConfigRule,
    selectedRuleId: string | null,
): string | null => {
    if (!selectedRuleId) {
        return null;
    }

    const allRules = SCORING_PRIORITIES.flatMap((priority) => rules[priority] ?? []);

    return allRules.some((rule) => rule.id === selectedRuleId) ? selectedRuleId : null;
};

export type OrderedScoringRule = {
    rule: IScoringRuleDto;
    index: number;
};

/**
 * Display order for one priority column: selected rules first, source order preserved within each
 * group. Every entry keeps its index in the source column because edit, delete and drag all resolve
 * a rule by that index.
 */
export const orderRulesSelectedFirst = (
    rules: readonly IScoringRuleDto[],
    selectedRuleIds: ReadonlySet<string> | undefined,
): readonly OrderedScoringRule[] => {
    const indexedRules = rules.map((rule, index) => ({ rule, index }));

    if (!selectedRuleIds?.size) {
        return indexedRules;
    }

    const isSelected = ({ rule }: OrderedScoringRule): boolean => !!rule.id && selectedRuleIds.has(rule.id);

    return [...indexedRules.filter(isSelected), ...indexedRules.filter((entry) => !isSelected(entry))];
};

export const findScoringRuleScope = (
    userRules: ScoringConfigRule,
    accountRules: ScoringConfigRule,
    ruleId: string,
): ScoringConfigType | undefined => {
    if (findScoringRuleById(accountRules, ruleId)) {
        return ScoringConfigType.Account;
    }

    if (findScoringRuleById(userRules, ruleId)) {
        return ScoringConfigType.User;
    }

    return undefined;
};

const findRuleIndexInScope = (rules: ScoringConfigRule, priority: LeadPriorityScore, ruleId: string): number =>
    (rules[priority] ?? []).findIndex((rule) => rule.id === ruleId);

const findFirstRule = (rules: ScoringConfigRule): { ruleId: string; priority: LeadPriorityScore } | undefined => {
    for (const priority of SCORING_PRIORITIES) {
        const ruleId = (rules[priority] ?? []).find((rule) => rule.id)?.id;

        if (ruleId) {
            return { ruleId, priority };
        }
    }

    return undefined;
};

export const removeRuleFromColumn = (
    rules: ScoringConfigRule,
    priority: LeadPriorityScore,
    ruleIndex: number,
): { nextRules: ScoringConfigRule; deletedRuleId?: string } => {
    const deletedRuleId = rules[priority]?.[ruleIndex]?.id;
    const column = (rules[priority] ?? []).filter((_, index) => index !== ruleIndex);

    return {
        nextRules: replaceScoringColumn(rules, priority, column),
        deletedRuleId,
    };
};

export const moveRuleBetweenColumns = (
    rules: ScoringConfigRule,
    sourcePriority: LeadPriorityScore,
    ruleIndex: number,
    targetPriority: LeadPriorityScore,
): { nextRules: ScoringConfigRule; movedRule: IScoringRuleDto } | undefined => {
    if (sourcePriority === targetPriority) {
        return undefined;
    }

    const rule = rules[sourcePriority]?.[ruleIndex];

    if (!rule) {
        return undefined;
    }

    const sourceColumn = (rules[sourcePriority] ?? []).filter((_, index) => index !== ruleIndex);
    const targetColumn = [...(rules[targetPriority] ?? []), rule];

    return {
        nextRules: replaceScoringColumn(
            replaceScoringColumn(rules, sourcePriority, sourceColumn),
            targetPriority,
            targetColumn,
        ),
        movedRule: rule,
    };
};

type UpsertRuleInColumnOptions = { mode: "create" } | { mode: "edit"; ruleIndex: number };

const upsertRuleInColumn = (
    rules: ScoringConfigRule,
    priority: LeadPriorityScore,
    rule: IScoringRuleDto,
    options: UpsertRuleInColumnOptions,
): ScoringConfigRule => {
    const priorityRules = rules[priority] ?? [];
    const nextColumn =
        options.mode === "create"
            ? [...priorityRules, rule]
            : priorityRules.map((existing, index) => (index === options.ruleIndex ? rule : existing));

    return replaceScoringColumn(rules, priority, nextColumn);
};

export const resolveScopedRuleLocation = (
    filteredRules: ScoringConfigRule,
    priority: LeadPriorityScore,
    ruleIndex: number,
    resolveRuleScope: (rule: IScoringRuleDto) => ScoringConfigType | undefined,
    getRulesForScope: (scope: ScoringConfigType) => ScoringConfigRule,
): { scope: ScoringConfigType; sourceIndex: number; rule: IScoringRuleDto } | undefined => {
    const rule = filteredRules[priority]?.[ruleIndex];

    if (!rule?.id) {
        return undefined;
    }

    const scope = resolveRuleScope(rule);

    if (!scope) {
        return undefined;
    }

    const sourceRules = getRulesForScope(scope);
    const sourceIndex = findRuleIndexInScope(sourceRules, priority, rule.id);

    if (sourceIndex < 0) {
        return undefined;
    }

    return { scope, sourceIndex, rule };
};

type WizardRuleUpdateState =
    | { mode: "create"; priority: LeadPriorityScore }
    | { mode: "edit"; priority: LeadPriorityScore; ruleIndex: number };

export const prepareWizardRuleUpdate = (
    rules: ScoringConfigRule,
    wizardState: WizardRuleUpdateState,
    rule: IScoringRuleDto,
): { nextRules: ScoringConfigRule; successKey: string; isCreate: boolean } => {
    const isCreate = wizardState.mode === "create";
    const nextRules = upsertRuleInColumn(
        rules,
        wizardState.priority,
        rule,
        isCreate ? { mode: "create" } : { mode: "edit", ruleIndex: wizardState.ruleIndex },
    );

    return {
        nextRules,
        isCreate,
        successKey: isCreate
            ? "si.pages.score_settings.toast.rule_created"
            : "si.pages.score_settings.toast.rule_updated",
    };
};

export const reconcileSelectedRuleAfterDelete = (
    deletedRuleId: string | undefined,
    effectiveSelectedRuleId: string | null | undefined,
    rules: ScoringConfigRule,
    onSelect: (ruleId: string, priority: LeadPriorityScore) => void,
): void => {
    if (!deletedRuleId || effectiveSelectedRuleId !== deletedRuleId) {
        return;
    }

    const firstRemainingRule = findFirstRule(rules);

    if (firstRemainingRule) {
        onSelect(firstRemainingRule.ruleId, firstRemainingRule.priority);
    }
};
